import React, { useCallback } from "react";
import type { Stage, StepContent } from "../types";
import { StageSection } from "./StageSection";
import { StepRenderer } from "./StepRenderer";
import { useToastStore } from "@/shared/stores/toastStore";
import { Payment } from "./Payment/Payment";
import { YooKassaWidget } from "./Payment/YooKassaWidget";
import { ServiceStatus } from "./ServiceStatus";
import { useServiceWorkflow } from "../hooks/useServiceWorkflow";
import { serviceConfig } from "../config/serviceConfig";
import { Card } from "@/shared/ui";
import styles from "./ServiceWorkflow.module.scss";
import { useServiceAccountStore } from "@/shared/stores/serviceAccountStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { usePayment } from "../hooks/usePayment";
import { pollTableStatus } from "@/shared/lib/polling";
import { useTableStatusStore } from "@/shared/stores/tableStatusStore";

interface ServiceWorkflowProps {
  renderIntro: (props: { setStage: (stage: Stage) => void }) => React.ReactNode;
  renderStageSpecific?: () => React.ReactNode;
}

const LoadingSpinner = () => (
  <Card variant="narrow">
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "300px",
      }}
    >
      <div className={styles.spinner}></div>
    </div>
  </Card>
);

export function ServiceWorkflow({
  renderIntro,
  renderStageSpecific,
}: ServiceWorkflowProps) {
  const { serviceType, pId, stage, navigateToStage } =
    useServiceWorkflowStore();
  const { serviceAccount } = useServiceAccountStore();
  const workflow = useServiceWorkflow();
  const payment = usePayment();
  const { showToast } = useToastStore();
  const config = serviceType ? serviceConfig[serviceType] : null;

  const resolveBlockValue = useCallback(
    (block: StepContent) => {
      if (block.flag === "email") return serviceAccount?.email || "";
      return block.value || "";
    },
    [serviceAccount],
  );

  const copyProps = { resolveBlockValue, onCopy: workflow.handleCopy };
  const { tableStatus } = useTableStatusStore();

  const componentMap: Record<string, React.ReactNode> = {
    loading: <LoadingSpinner />,
    status: (
      <ServiceStatus
        statusInfo={workflow.tableStatus}
        onPay={() => navigateToStage("payment")}
        onUpdate={() =>
          navigateToStage(
            serviceType === "auto_jam" ? "select_table" : "stage_1",
          )
        }
        onChangePayment={() => {}}
        onCancel={() => {}}
        onCopy={workflow.handleCopy}
        onDelete={workflow.handleDeleteRequest}
        onUnsubscribe={workflow.handleUnsubscribeRequest}
      />
    ),
    payment_widget: pId && (
      <YooKassaWidget
        method={"yookassa"}
        tariffId={workflow.tariff?.id || null}
        promocode={payment.isPromoValid ? payment.promocode : null}
        email={payment.email}
        onError={(message) => {
          showToast(message, "error");
          navigateToStage("payment");
        }}
        onSuccess={async () => {
          if (tableStatus?.tableId) {
            try {
              await pollTableStatus(tableStatus.tableId, {
                isReady: (subscriptionType) => subscriptionType === "paid",
              });
              await workflow.fetchTableStatus();
              showToast("Оплата успешно произведена", "success");
            } catch {
              await workflow.fetchTableStatus();
              showToast(
                "Оплата прошла, но статус пока не обновился. Пожалуйста, проверьте через минуту.",
                "info",
                5000,
              );
            }
          }
          navigateToStage("status");
        }}
      />
    ),
    intro: renderIntro({
      setStage: navigateToStage,
    }),
    stage_1: (
      <Card variant="narrow">
        <StageSection
          title="Инструкция"
          isMainTitle
          steps={config ? config.steps_1 : []}
          StepRenderer={StepRenderer}
          copyProps={copyProps}
          next={() => navigateToStage("stage_2")}
        />
      </Card>
    ),
    stage_2: (
      <Card variant="narrow">
        <StageSection
          title="Инструкция"
          isMainTitle
          steps={config ? config.steps_2 : []}
          StepRenderer={StepRenderer}
          copyProps={copyProps}
          input={{
            tableUrl: workflow.tableUrl,
            setTableUrl: workflow.setTableUrl,
          }}
          onSubmit={workflow.handleConnectTable}
          isConnecting={workflow.isConnecting}
        />
      </Card>
    ),
    payment: pId && (
      <Payment
        email={payment.email}
        setEmail={payment.setEmail}
        emailError={payment.emailError}
        setEmailError={payment.setEmailError}
        promocode={payment.promocode}
        setPromocode={payment.setPromocode}
        promoError={payment.promoError}
        promoSuccess={payment.promoSuccess}
        orderPrice={payment.orderPrice}
        isProcessing={payment.isProcessing}
        handleApplyPromocode={payment.handleApplyPromocode}
        handleProceedToPayment={payment.handleProceedToPayment}
        handleNavigateBack={() => navigateToStage("stage_2")}
      />
    ),
  };

  const renderCurrentStage = () => {
    const stageSpecificComponent = renderStageSpecific && renderStageSpecific();
    if (stageSpecificComponent) {
      return stageSpecificComponent;
    }

    const component = componentMap[stage];
    if (component) {
      return component;
    }

    return null;
  };

  return <>{renderCurrentStage()}</>;
}
